import React, { useState, useRef, useEffect } from 'react';
import { Camera, Plus, Receipt, CheckCircle2, Trash2, Utensils, Beer, Pencil } from 'lucide-react';
import { Button } from '../../components/Button';
import { UserAvatarGroup } from '../../components/UserAvatarGroup';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currencyFormatting';
import type { Tab, TabItem, UserProfile, ManualMember } from '../../types';

interface TabItemsListProps {
    tab: Tab;
    userId: string | undefined;
    isOwner: boolean;
    onScan: () => void;
    onAdd: () => void;
    onUpdateClaim?: (itemId: string, action: 'toggle' | 'increment' | 'decrement' | 'set') => void;
    onDelete?: (itemId: string) => void;
    onEdit?: (item: TabItem) => void;
    onViewItemSharers: (item: TabItem) => void;
    resolveItemSharers: (claimedBy: string[]) => { registered: UserProfile[], manual: ManualMember[] };
    isAuthenticated: boolean;
}

const SwipeableTabItem = ({
    item,
    tab,
    userId,
    isOwner,
    onUpdateClaim,
    onDelete,
    onEdit,
    onViewItemSharers,
    resolveItemSharers,
}: {
    item: TabItem,
    tab: Tab,
    userId: string | undefined,
    isOwner: boolean,
    onUpdateClaim?: TabItemsListProps['onUpdateClaim'],
    onDelete?: TabItemsListProps['onDelete'],
    onEdit?: TabItemsListProps['onEdit'],
    onViewItemSharers: TabItemsListProps['onViewItemSharers'],
    resolveItemSharers: TabItemsListProps['resolveItemSharers'],
}) => {
    const quantity = item.quantity ?? 1;
    const claimedCount = item.claimedBy.length;
    const isFullyClaimed = quantity > 1 && claimedCount >= quantity;
    const myClaims = item.claimedBy.filter(uid => uid === userId).length;
    const isClaimedByMe = myClaims > 0;
    const { registered, manual } = resolveItemSharers(item.claimedBy);

    // Swipe and Undo State
    const [offset, setOffset] = useState(0);
    const [isSwiping, setIsSwiping] = useState(false);
    const [isPendingDelete, setIsPendingDelete] = useState(false);

    const startX = useRef(0);
    const currentX = useRef(0);
    const deleteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const swipeEnabled = isOwner && !!onDelete;
    const threshold = -60;

    const handlePointerDown = (e: React.PointerEvent) => {
        if (!swipeEnabled || isPendingDelete) return;
        setIsSwiping(true);
        startX.current = e.clientX;
        currentX.current = e.clientX;
        // setPointerCapture can swallow child click events on some mobile browsers
        // e.currentTarget.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!isSwiping || !swipeEnabled) return;
        currentX.current = e.clientX;
        const diff = currentX.current - startX.current;
        // Only allow swiping left
        if (diff < 0) {
            setOffset(diff);
        } else {
            setOffset(0);
        }
    };

    const handlePointerUp = () => {
        if (!isSwiping) return;
        setIsSwiping(false);
        if (offset < threshold) {
            setIsPendingDelete(true);
            setOffset(0);
            deleteTimeoutRef.current = setTimeout(() => {
                onDelete?.(item.id);
            }, 4000);
        } else {
            setOffset(0);
        }
    };

    const handleUndo = () => {
        setIsPendingDelete(false);
        if (deleteTimeoutRef.current) {
            clearTimeout(deleteTimeoutRef.current);
        }
    };

    useEffect(() => {
        return () => {
            if (deleteTimeoutRef.current) clearTimeout(deleteTimeoutRef.current);
        };
    }, []);

    const handleItemClick = () => {
        // Prevent click if we were swiping (allow 15px of finger jiggle for normal taps)
        if (Math.abs(currentX.current - startX.current) > 15) return;
        onViewItemSharers(item);
    };

    if (isPendingDelete) {
        return (
            <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-3 min-h-[52px] animate-in fade-in zoom-in-95 duration-200">
                <div className="flex items-center gap-2 text-red-600 truncate mr-2">
                    <Trash2 size={16} className="shrink-0" />
                    <span className="text-sm font-medium truncate">Deleted "{item.name}"</span>
                </div>
                <Button variant="ghost" size="sm" className="h-7 text-red-600 font-bold hover:bg-red-100 shrink-0" onClick={handleUndo}>
                    UNDO
                </Button>
            </div>
        );
    }

    return (
        <div
            className="group relative rounded-lg overflow-hidden touch-pan-y min-h-[52px]"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
        >
            {/* Background Red Delete Area */}
            {swipeEnabled && (
                <div className={cn("absolute inset-[1px] bg-red-500 flex items-center justify-end pr-6 text-white rounded-lg transition-opacity duration-200", offset < 0 ? "opacity-100" : "opacity-0")}>
                    <Trash2 size={20} className={cn("transition-transform duration-200", offset < threshold ? "scale-110" : "scale-100 opacity-70")} />
                </div>
            )}

            {/* Foreground Item Content */}
            <div
                className={cn(
                    "relative flex items-center gap-3 border p-2 min-h-[52px] rounded-lg",
                    isSwiping ? "transition-none" : "transition-transform duration-200",
                    isClaimedByMe ? "border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/30 shadow-sm" : "border-border-default bg-bg-card",
                    isFullyClaimed && "opacity-75 bg-slate-50 dark:bg-slate-900/50",
                    !onUpdateClaim && "opacity-80"
                )}
                style={{ transform: `translateX(${offset}px)` }}
            >
                {/* Left: Check Icon or Counter (Isolated Hitbox) */}
                <div className="flex-none flex items-center justify-center w-8">
                    {quantity === 1 ? (
                        <button
                            className={cn(
                                "flex h-8 w-8 items-center justify-center rounded-full transition-all hover:ring-4 hover:ring-primary-100 dark:hover:ring-primary-900/50 disabled:opacity-50",
                                isClaimedByMe ? "bg-primary-500 text-white border-primary-500" : "border-2 border-border-default bg-white dark:bg-bg-card"
                            )}
                            onClick={(e) => {
                                e.stopPropagation();
                                onUpdateClaim?.(item.id, 'toggle');
                            }}
                            disabled={!onUpdateClaim}
                        >
                            {isClaimedByMe && <CheckCircle2 size={16} />}
                        </button>
                    ) : (
                        <div className="flex flex-col items-center gap-1">
                            <button
                                className="h-6 w-6 flex items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/80 disabled:opacity-30 disabled:cursor-not-allowed"
                                onClick={(e) => { e.stopPropagation(); onUpdateClaim?.(item.id, 'increment'); }}
                                disabled={claimedCount >= quantity || !onUpdateClaim}
                            >
                                <Plus size={14} />
                            </button>
                            <span
                                className="text-[10px] font-bold text-text-muted leading-none cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 px-1 rounded"
                                onClick={(e) => { e.stopPropagation(); onUpdateClaim?.(item.id, 'set'); }}
                            >
                                {claimedCount}/{quantity}
                            </span>
                            <button
                                className="h-6 w-6 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                                onClick={(e) => { e.stopPropagation(); onUpdateClaim?.(item.id, 'decrement'); }}
                                disabled={!isClaimedByMe || !onUpdateClaim}
                            >
                                <div className="w-2 h-0.5 bg-current rounded-full" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Middle: Clickable Assignment Area */}
                <button
                    className="flex-1 min-w-0 flex items-center justify-between cursor-pointer rounded-md hover:bg-black/5 dark:hover:bg-white/5 -m-1 p-1 transition-colors text-left"
                    onClick={handleItemClick}
                    type="button"
                >
                    <div className="flex flex-col justify-center pr-2 flex-1 min-w-0">
                        <div className="flex items-center gap-1 min-w-0">
                            {item.category === 'Food' && <Utensils size={14} className="text-orange-500 flex-none" />}
                            {item.category === 'Drink' && <Beer size={14} className="text-blue-500 flex-none" />}
                            <p className={cn(
                                "text-sm font-bold text-text-heading leading-tight truncate",
                                isFullyClaimed && "line-through text-text-muted"
                            )}>{item.name}</p>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 min-h-[20px]">
                            {quantity > 1 && (
                                <div className="text-[10px] text-text-muted whitespace-nowrap">
                                    {quantity}x {formatCurrency(item.price / quantity, tab.currency)}
                                </div>
                            )}

                            {item.claimedBy.length > 0 ? (
                                <div className="py-0.5 inline-flex items-center">
                                    <UserAvatarGroup
                                        registeredUsers={registered}
                                        manualUsers={manual}
                                        maxDisplay={3}
                                        size="xs"
                                    />
                                </div>
                            ) : (
                                <span className="text-[10px] text-text-muted opacity-75 whitespace-nowrap">
                                    {quantity > 1 ? "Assign claims" : "Assign to someone"}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="flex-none text-right px-2">
                        <p className="text-sm font-black text-text-heading">
                            {formatCurrency(item.price, tab.currency)}
                        </p>
                        {isClaimedByMe && (
                            <p className="text-[10px] text-primary-600 dark:text-primary-400 font-bold leading-tight">
                                Your share: {
                                    quantity > 1
                                        ? formatCurrency((item.price / quantity) * myClaims, tab.currency)
                                        : formatCurrency(item.price / item.claimedBy.length, tab.currency)
                                }
                            </p>
                        )}
                    </div>
                </button>

                {/* Right: Dedicated Edit Button */}
                {onEdit && (
                    <div className="flex-none flex items-center justify-center border-l pl-2 border-border-default/50">
                        <button
                            onClick={(e) => { e.stopPropagation(); onEdit(item); }}
                            className="p-2 text-text-faint hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors focus:opacity-100"
                            aria-label="Edit Item"
                        >
                            <Pencil size={16} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export const TabItemsList: React.FC<TabItemsListProps> = (props) => {
    const { tab, isAuthenticated, onScan, onAdd } = props;

    return (
        <div className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-4 pb-44">
            <div className="flex items-center justify-between">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-text-faint">Bill Items</h3>
                <div className="flex gap-1">
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-primary-600 font-bold relative disabled:opacity-50"
                        onClick={onScan}
                        disabled={!isAuthenticated}
                        title={!isAuthenticated ? "You must be signed in to scan receipts" : ""}
                    >
                        <Camera size={16} className="mr-1" /> Scan
                        <span className="absolute -top-1 -right-1 bg-primary-600 text-[8px] text-white px-1 rounded-full leading-tight">Alpha</span>
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-primary-600 font-bold" onClick={onAdd}>
                        <Plus size={16} className="mr-1" /> Add
                    </Button>
                </div>
            </div>

            {tab.items.length === 0 ? (
                <div className="rounded-3xl border-2 border-dashed border-border-default p-10 text-center">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-bg-base text-text-faint">
                        <Receipt size={24} />
                    </div>
                    <p className="text-sm font-medium text-text-muted">The bill is empty</p>
                    <Button variant="ghost" size="sm" className="mt-2 text-primary-600" onClick={onAdd}>
                        Add the first item
                    </Button>
                </div>
            ) : (
                <div className="space-y-3 pb-4">
                    {tab.items.map((item) => (
                        <SwipeableTabItem
                            key={`${item.id}-${item.claimedBy.length}`}
                            item={item}
                            {...props}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
